import { useState } from "react";
import { api } from "../api";
import { Avatar } from "./Posts";
import { toast } from "sonner";

// §11-20 "+ Aggiungi paziente": trova un paziente iscritto (ricerca deterministica Nome+Cognome+CF)
// oppure invita un nuovo paziente (controllo duplicati sul CF). Nessuna email privata mostrata nei risultati.
export function AddPatientModal({ onClose }) {
  const [mode, setMode] = useState("find"); // find | invite
  const [f, setF] = useState({ first_name: "", last_name: "", tax_id: "", email: "", birth_date: "" });
  const [result, setResult] = useState(null); // { found, patient, already_related, request_pending }
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const search = async () => {
    setMsg(null); setResult(null);
    if (!f.first_name.trim() || !f.last_name.trim() || f.tax_id.trim().length < 11)
      return toast.error("Inserisci Nome, Cognome e Codice fiscale completo");
    setBusy(true);
    try {
      const { data } = await api.get("/clinical/patient-search", { params: { first_name: f.first_name.trim(), last_name: f.last_name.trim(), tax_id: f.tax_id.trim() } });
      if (!data.found) setMsg({ type: "err", text: "Nessun paziente corrisponde esattamente a questi dati." });
      else setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail === "org_not_clinical" ? "Struttura senza attività clinica" : "Errore nella ricerca");
    } finally { setBusy(false); }
  };

  const sendRequest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/clinical/patient-request", { patient_id: result.patient.id });
      if (data.status === "already_related") toast.success("Relazione clinica già attiva");
      else if (data.status === "pending") toast.info("Richiesta già inviata, in attesa di accettazione");
      else toast.success("Richiesta clinica inviata");
      onClose?.();
    } catch { toast.error("Errore invio richiesta"); }
    finally { setBusy(false); }
  };

  const invite = async () => {
    setMsg(null);
    if (!f.first_name.trim() || !f.last_name.trim() || f.tax_id.trim().length < 11 || !f.email.trim())
      return toast.error("Compila Nome, Cognome, Codice fiscale ed Email");
    setBusy(true);
    try {
      const { data } = await api.post("/clinical/patient-invite", {
        first_name: f.first_name.trim(), last_name: f.last_name.trim(),
        tax_id: f.tax_id.trim(), email: f.email.trim(), birth_date: f.birth_date || undefined });
      if (data.already_registered) {
        if (data.status === "already_related") setMsg({ type: "ok", text: "Questo paziente risulta già registrato su YouPAM. Relazione clinica già attiva." });
        else setMsg({ type: "ok", text: "Questo paziente risulta già registrato su YouPAM. Abbiamo inviato una richiesta clinica al suo account esistente." });
      } else {
        setMsg({ type: "ok", text: "Invito inviato. Il paziente completerà la registrazione e la relazione clinica verrà attivata automaticamente." });
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "cf_not_patient" ? "Questo Codice fiscale appartiene a un account non paziente"
        : d === "org_not_clinical" ? "Struttura senza attività clinica" : "Errore durante l'invito");
    } finally { setBusy(false); }
  };

  return (<div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-patient-modal">
    <div className="bg-white rounded-xl w-full max-w-md p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
      <div className="flex justify-between items-center"><h3 className="font-semibold">Aggiungi paziente</h3><button onClick={onClose} data-testid="add-patient-close">✕</button></div>
      <div className="flex gap-2 border-b border-slate-200">
        <button className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${mode === "find" ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500"}`} onClick={() => { setMode("find"); setMsg(null); setResult(null); }} data-testid="add-patient-tab-find">Trova paziente iscritto</button>
        <button className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px ${mode === "invite" ? "border-blue-500 text-blue-600" : "border-transparent text-slate-500"}`} onClick={() => { setMode("invite"); setMsg(null); setResult(null); }} data-testid="add-patient-tab-invite">Invita nuovo paziente</button>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <input className="yp-input" placeholder="Nome" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="add-patient-first" />
        <input className="yp-input" placeholder="Cognome" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="add-patient-last" />
        <input className="yp-input col-span-2 uppercase" placeholder="Codice fiscale" value={f.tax_id} onChange={(e) => set("tax_id", e.target.value.toUpperCase())} data-testid="add-patient-cf" />
        {mode === "invite" && <>
          <input className="yp-input col-span-2" type="email" placeholder="Email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="add-patient-email" />
          <label className="col-span-2 text-xs text-slate-500 -mb-1" htmlFor="add-patient-bdate">Data di nascita (facoltativa)</label>
          <input id="add-patient-bdate" className="yp-input col-span-2" type="date" value={f.birth_date} onChange={(e) => set("birth_date", e.target.value)} data-testid="add-patient-bdate" />
        </>}
      </div>

      {mode === "find" && !result && (
        <button className="yp-btn yp-btn-primary w-full" onClick={search} disabled={busy} data-testid="add-patient-search-btn">{busy ? "Ricerca…" : "Cerca paziente"}</button>)}

      {mode === "find" && result && (
        <div className="border border-emerald-200 bg-emerald-50 rounded p-3 space-y-2" data-testid="add-patient-result">
          <div className="flex items-center gap-3">
            <Avatar url={result.patient.avatar_url} label={`${result.patient.first_name} ${result.patient.last_name}`} cls="w-10 h-10 text-sm" />
            <div><div className="font-medium">{result.patient.first_name} {result.patient.last_name}</div><div className="text-xs text-slate-500">Identità verificata tramite Codice fiscale</div></div>
          </div>
          {result.already_related ? <div className="text-xs text-emerald-700">Relazione clinica già attiva.</div>
            : result.request_pending ? <div className="text-xs text-slate-600">Richiesta clinica già inviata, in attesa di accettazione.</div>
            : <button className="yp-btn yp-btn-primary w-full" onClick={sendRequest} disabled={busy} data-testid="add-patient-request-btn">Invia richiesta clinica</button>}
          <button className="text-xs text-slate-500 underline" onClick={() => setResult(null)} data-testid="add-patient-search-again">Nuova ricerca</button>
        </div>)}

      {mode === "invite" && (
        <button className="yp-btn yp-btn-primary w-full" onClick={invite} disabled={busy} data-testid="add-patient-invite-btn">{busy ? "Invio…" : "Invia invito / richiesta"}</button>)}

      {msg && <div className={`text-xs ${msg.type === "err" ? "text-red-600" : "text-emerald-700"}`} data-testid="add-patient-msg">{msg.text}</div>}
    </div>
  </div>);
}
